/**
 * Presentational form controls for the onboarding wizard. No business logic:
 * parents own the state and pass value + onChange + error. Keeps every step
 * short and the styling in one place (partnerOnboarding.scss, "pw-" prefix).
 */

import React from "react"

export const Field = ({ label, required, error, hint, children, full }) => (
  <label className={`pw-field${full ? " pw-field--full" : ""}${error ? " pw-field--error" : ""}`}>
    {label && (
      <span className="pw-field__label">
        {label}
        {required && <span className="pw-field__req"> *</span>}
      </span>
    )}
    {children}
    {error ? (
      <span className="pw-field__msg">{error}</span>
    ) : hint ? (
      <span className="pw-field__hint">{hint}</span>
    ) : null}
  </label>
)

export const TextInput = ({ value, onChange, name, ...rest }) => (
  <input
    className="pw-input"
    value={value ?? ""}
    onChange={(e) => onChange(name, e.target.value)}
    {...rest}
  />
)

export const NumberInput = ({ value, onChange, name, ...rest }) => (
  <input
    type="number"
    inputMode="decimal"
    className="pw-input"
    value={value ?? ""}
    onChange={(e) => onChange(name, e.target.value)}
    {...rest}
  />
)

export const SelectInput = ({ value, onChange, name, options, placeholder = "Select", ...rest }) => (
  <select
    className={`pw-input pw-select${!value ? " pw-select--placeholder" : ""}`}
    value={value ?? ""}
    onChange={(e) => onChange(name, e.target.value)}
    {...rest}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.value
      const lbl = typeof o === "string" ? o : o.label
      return <option key={val} value={val}>{lbl}</option>
    })}
  </select>
)

/** Big selectable cards (business type). One choice. */
export const RadioCards = ({ value, onChange, name, options }) => (
  <div className="pw-cards">
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        className={`pw-card${value === o.value ? " pw-card--active" : ""}`}
        onClick={() => onChange(name, o.value)}
      >
        <span className="pw-card__title">{o.label}</span>
        {o.hint && <span className="pw-card__hint">{o.hint}</span>}
        <span className="pw-card__tick" aria-hidden>&#10003;</span>
      </button>
    ))}
  </div>
)

/** Multi-select chips (cab types offered). */
export const CheckChips = ({ values = [], onToggle, options }) => (
  <div className="pw-chips">
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.value
      const lbl = typeof o === "string" ? o : o.label
      const active = values.includes(val)
      return (
        <button
          type="button"
          key={val}
          className={`pw-chip${active ? " pw-chip--active" : ""}`}
          onClick={() => onToggle(val)}
        >
          {active && <span className="pw-chip__tick" aria-hidden>&#10003; </span>}
          {lbl}
        </button>
      )
    })}
  </div>
)

/** Single document upload row: pick, show progress, replace or remove. */
export const FileField = ({ doc, status, meta, onSelect, onRemove }) => {
  const inputRef = React.useRef(null)
  const uploaded = status === "done"
  const uploading = status === "uploading"
  const failed = status === "error"

  return (
    <div className={`pw-file${uploaded ? " pw-file--done" : ""}${failed ? " pw-file--error" : ""}`}>
      <div className="pw-file__meta">
        <span className="pw-file__label">
          {doc.label}
          {doc.required && <span className="pw-field__req"> *</span>}
        </span>
        {uploaded && meta?.filename && (
          <span className="pw-file__name">{meta.filename}</span>
        )}
        {failed && <span className="pw-file__name pw-file__name--err">Upload failed, try again</span>}
      </div>

      <div className="pw-file__actions">
        {uploading ? (
          <span className="pw-file__spinner" aria-label="Uploading">Uploading...</span>
        ) : uploaded ? (
          <>
            <span className="pw-file__badge" aria-hidden>&#10003;</span>
            <button type="button" className="pw-file__link" onClick={() => inputRef.current?.click()}>Replace</button>
            <button type="button" className="pw-file__link pw-file__link--muted" onClick={onRemove}>Remove</button>
          </>
        ) : (
          <button type="button" className="pw-file__btn" onClick={() => inputRef.current?.click()}>
            Upload
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={doc.accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onSelect(f)
          e.target.value = ""
        }}
      />
    </div>
  )
}
